/**
 * Error Mapper Utility
 *
 * Classifies technical errors and maps them to user-friendly messages.
 * All messages use i18n translation keys with English fallbacks.
 *
 * Usage:
 *   const mapped = mapError(error);
 *   toast.error(mapped.translationKey, mapped.fallback);
 */

// Error categories for classification
export type ErrorCategory =
  | 'network'      // Connection issues, offline, timeout
  | 'server'       // 5xx server errors
  | 'unauthorized' // 401 - session expired
  | 'forbidden'    // 403 - no permission
  | 'notFound'     // 404 - resource not found
  | 'validation'   // 400 - bad request / validation errors
  | 'rateLimited'  // 429 - too many requests
  | 'conflict'     // 409 - resource conflict (e.g., slot already booked)
  | 'unknown';     // Fallback for unclassified errors

// Result of error mapping
export interface MappedError {
  category: ErrorCategory;
  translationKey: string;
  fallback: string;
  originalMessage?: string;
  statusCode?: number;
}

// Type guard for axios-like errors
interface AxiosLikeError {
  response?: {
    status?: number;
    data?: {
      message?: string;
      error?: string;
      errorCode?: string;
      code?: string; // Custom error code (e.g., 'CURRENCY_MISMATCH')
      existingCurrency?: string;
      requestedCurrency?: string;
    };
  };
  code?: string;
  message?: string;
}

/**
 * Get custom error code from API response
 */
export function getErrorCode(error: unknown): string | undefined {
  if (!error) return undefined;

  const axiosError = error as AxiosLikeError;
  return axiosError.response?.data?.code || axiosError.response?.data?.errorCode;
}

// Common network error patterns
const NETWORK_ERROR_PATTERNS = [
  'network error',
  'err_network',
  'econnaborted',
  'econnrefused',
  'econnreset',
  'etimedout',
  'timeout',
  'net::err',
  'failed to fetch',
  'load failed',
  'networkerror',
];

// Common timeout error patterns
const TIMEOUT_PATTERNS = [
  'timeout',
  'econnaborted',
  'etimedout',
  'request timeout',
];

/**
 * Check if the error is network-related (offline, connection failed, etc.)
 */
export function isNetworkError(error: unknown): boolean {
  if (!error) return false;

  // Check if browser is offline
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return true;
  }

  const axiosError = error as AxiosLikeError;

  // No response usually means network failure
  if (axiosError.response === undefined && axiosError.message) {
    const messageLower = axiosError.message.toLowerCase();
    return NETWORK_ERROR_PATTERNS.some(pattern => messageLower.includes(pattern));
  }

  // Check error code
  if (axiosError.code) {
    const codeLower = axiosError.code.toLowerCase();
    return NETWORK_ERROR_PATTERNS.some(pattern => codeLower.includes(pattern));
  }

  return false;
}

/**
 * Check if the error is a timeout
 */
export function isTimeoutError(error: unknown): boolean {
  if (!error) return false;

  const axiosError = error as AxiosLikeError;

  if (axiosError.code) {
    const codeLower = axiosError.code.toLowerCase();
    if (TIMEOUT_PATTERNS.some(pattern => codeLower.includes(pattern))) {
      return true;
    }
  }

  if (axiosError.message) {
    const messageLower = axiosError.message.toLowerCase();
    if (TIMEOUT_PATTERNS.some(pattern => messageLower.includes(pattern))) {
      return true;
    }
  }

  return false;
}

/**
 * Extract HTTP status code from error
 */
export function getStatusCode(error: unknown): number | undefined {
  if (!error) return undefined;

  const axiosError = error as AxiosLikeError;
  return axiosError.response?.status;
}

/**
 * Extract original error message (for logging, not for display)
 */
export function getOriginalMessage(error: unknown): string | undefined {
  if (!error) return undefined;

  const axiosError = error as AxiosLikeError;

  return (
    axiosError.response?.data?.message ||
    axiosError.response?.data?.error ||
    axiosError.message ||
    (typeof error === 'string' ? error : undefined)
  );
}

/**
 * Main function: Classify and map an error to user-friendly message
 */
export function mapError(error: unknown): MappedError {
  const statusCode = getStatusCode(error);
  const originalMessage = getOriginalMessage(error);
  const errorCode = getErrorCode(error);

  // 1. Check for network/offline errors first
  if (isNetworkError(error)) {
    return {
      category: 'network',
      translationKey: 'common:errors.network',
      fallback: "Can't connect. Check your internet connection",
      originalMessage,
      statusCode,
    };
  }

  // 2. Check for timeout errors
  if (isTimeoutError(error)) {
    return {
      category: 'network',
      translationKey: 'common:errors.timeout',
      fallback: 'Request timed out. Please try again',
      originalMessage,
      statusCode,
    };
  }

  // 3. Check for specific error codes (before generic status code mapping)
  if (errorCode) {
    switch (errorCode) {
      case 'CURRENCY_MISMATCH':
        return {
          category: 'validation',
          translationKey: 'billing:currencyMismatch.message',
          fallback: 'Cannot change billing currency while you have an active subscription. Please cancel your current subscription first.',
          originalMessage,
          statusCode,
        };

      // The saved card was declined during a plan change. The plan was NOT
      // changed, so the fix is to update the card and retry.
      case 'PAYMENT_FAILED':
        return {
          category: 'validation',
          translationKey: 'billing:paymentFailedRetry',
          fallback: 'Your payment could not be completed. Please update your payment method and try again.',
          originalMessage,
          statusCode,
        };
    }
  }

  // 4. Map by HTTP status code
  if (statusCode) {
    switch (statusCode) {
      case 400:
        return {
          category: 'validation',
          translationKey: 'common:errors.validation',
          fallback: 'Please check your input and try again',
          originalMessage,
          statusCode,
        };

      case 401:
        return {
          category: 'unauthorized',
          translationKey: 'common:errors.sessionExpired',
          fallback: 'Your session has expired. Please sign in again',
          originalMessage,
          statusCode,
        };

      case 403:
        return {
          category: 'forbidden',
          translationKey: 'common:errors.noPermission',
          fallback: "You don't have permission to do this",
          originalMessage,
          statusCode,
        };

      case 404:
        return {
          category: 'notFound',
          translationKey: 'common:errors.notFound',
          fallback: "The item you're looking for doesn't exist",
          originalMessage,
          statusCode,
        };

      case 409:
        return {
          category: 'conflict',
          translationKey: 'common:errors.conflict',
          fallback: 'This action conflicts with existing data. Please refresh and try again',
          originalMessage,
          statusCode,
        };

      case 429:
        return {
          category: 'rateLimited',
          translationKey: 'common:errors.rateLimited',
          fallback: 'Too many requests. Please wait a moment',
          originalMessage,
          statusCode,
        };

      default:
        // 5xx server errors
        if (statusCode >= 500 && statusCode < 600) {
          return {
            category: 'server',
            translationKey: 'common:errors.serverError',
            fallback: 'Something went wrong on our end. Please try again',
            originalMessage,
            statusCode,
          };
        }
    }
  }

  // 5. Fallback: unknown error
  return {
    category: 'unknown',
    translationKey: 'common:errors.generic',
    fallback: 'Something went wrong. Please try again',
    originalMessage,
    statusCode,
  };
}

/**
 * Check if error should show as a banner (more prominent) vs regular toast
 * Network and server errors are shown as banners since they affect the whole app
 */
export function shouldShowAsBanner(error: unknown): boolean {
  const mapped = mapError(error);
  return mapped.category === 'network' || mapped.category === 'server';
}

/**
 * Get a simple user-friendly message without i18n (for edge cases)
 */
export function getSimpleErrorMessage(error: unknown): string {
  const mapped = mapError(error);
  return mapped.fallback;
}
